import { useCallback, useMemo, useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import { monthLabel } from "@/lib/availability/months";
import { cn } from "@/lib/utils";
import { CellTooltip, useGridTooltip } from "./CellTooltip";
import { GroupRow, ResourceRow } from "./GridRows";
import { columnEdge, edgeClass, type GridLayout } from "./grid-layout";
import { type GridGroup, type MonthHeaderKind, monthHeaderKind, type ViewRow } from "./model";
import type { Selection } from "./search-params";

const HEADER_SUB: Record<MonthHeaderKind, string> = {
  actual: "ACTUAL",
  mtd: "MTD",
  forecast: "FORECAST",
  partial: "PARTIAL",
};

/** `Oct`, or `Oct '26` on the first column and every January, so the year is never lost. */
function columnLabel(month: string, index: number): string {
  const short = monthLabel(month, { short: true });
  return index === 0 || month.slice(5, 7) === "01" ? `${short} '${month.slice(2, 4)}` : short;
}

type DisplayRow =
  | { kind: "group"; group: GridGroup; open: boolean }
  | { kind: "resource"; row: ViewRow };

/** The rows in the order they are drawn, with collapsed groups' people left out. */
function flatten(
  groups: readonly GridGroup[],
  showGroups: boolean,
  isOpen: (groupId: string) => boolean,
): DisplayRow[] {
  const rows: DisplayRow[] = [];
  for (const group of groups) {
    const open = !showGroups || isOpen(group.id);
    if (showGroups) rows.push({ kind: "group", group, open });
    if (open) for (const row of group.rows) rows.push({ kind: "resource", row });
  }
  return rows;
}

interface AvailabilityGridProps {
  groups: readonly GridGroup[];
  layout: GridLayout;
  /** False for "No grouping": one flat list with no group header rows. */
  showGroups: boolean;
  isOpen: (groupId: string) => boolean;
  onToggleGroup: (groupId: string) => void;
  selection: Selection | null;
  onSelectResource: (resourceId: string, month: string) => void;
  onSelectGroup: (groupKey: string | null, month: string) => void;
  /** Rows left after filters, to tell "nothing matches" from "all groups collapsed". */
  visibleCount: number;
  onClearFilters: () => void;
}

/**
 * The Resource × month grid. The header row and the Resource column are sticky.
 * Every cell is a real button; arrow keys move between cells (one Tab stop for the
 * whole grid), and a hover or focus opens one shared tooltip.
 *
 * Not virtualized: `@tanstack/react-virtual` is not a dependency of this app, and
 * above 150 Resources the groups load collapsed, so a 1,000-Resource view starts as
 * a few dozen group rows.
 */
export function AvailabilityGrid({
  groups,
  layout,
  showGroups,
  isOpen,
  onToggleGroup,
  selection,
  onSelectResource,
  onSelectGroup,
  visibleCount,
  onClearFilters,
}: AvailabilityGridProps) {
  const { tip, show, hide } = useGridTooltip();
  const [focus, setFocus] = useState({ row: 0, col: 0 });

  const rows = useMemo(() => flatten(groups, showGroups, isOpen), [groups, showGroups, isOpen]);
  const tabRow = Math.min(focus.row, Math.max(rows.length - 1, 0));
  const lastCol = layout.columns.length - 1;

  const onFocusCapture = useCallback((event: React.FocusEvent<HTMLElement>) => {
    const { availRow, availCol } = event.target.dataset;
    if (availRow === undefined || availCol === undefined) return;
    const row = Number(availRow);
    const col = Number(availCol);
    setFocus((prev) => (prev.row === row && prev.col === col ? prev : { row, col }));
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const { availRow, availCol } = target.dataset;
      if (availRow === undefined || availCol === undefined) return;
      let row = Number(availRow);
      let col = Number(availCol);
      switch (event.key) {
        case "ArrowRight":
          col = Math.min(col + 1, lastCol);
          break;
        case "ArrowLeft":
          col = Math.max(col - 1, 0);
          break;
        case "ArrowDown":
          row += 1;
          break;
        case "ArrowUp":
          row -= 1;
          break;
        case "Home":
          col = 0;
          break;
        case "End":
          col = lastCol;
          break;
        default:
          return;
      }
      const next = event.currentTarget.querySelector<HTMLElement>(
        `[data-avail-row="${row}"][data-avail-col="${col}"]`,
      );
      if (next !== null) {
        event.preventDefault();
        next.focus();
      }
    },
    [lastCol],
  );

  const selectedResource = selection?.kind === "resource" ? selection : null;
  const selectedGroup = selection?.kind === "group" ? selection : null;

  return (
    <>
      <section
        aria-label="Availability by Resource and month"
        className="max-h-[70vh] overflow-auto rounded-lg border bg-card"
      >
        <div
          style={{ minWidth: layout.minWidthPx }}
          onKeyDown={onKeyDown}
          onFocusCapture={onFocusCapture}
        >
          <div
            className="sticky top-0 z-30 grid border-b bg-card"
            style={{ gridTemplateColumns: layout.template, columnGap: layout.columnGapPx }}
          >
            <div className="sticky left-0 z-40 flex items-end bg-card px-4 py-2.5 font-mono text-xs font-medium tracking-wider text-muted-foreground uppercase">
              Resource
            </div>
            <div className="flex flex-col justify-end gap-0.5 px-2 py-2.5">
              <span className="text-sm font-semibold text-heading">Net in window</span>
              <span className="font-mono text-xs tracking-wider text-muted-foreground">
                FREE · OVER
              </span>
            </div>
            {layout.lastThreeMonths.length > 0 ? (
              <div className="flex flex-col justify-end gap-0.5 px-2 py-2.5">
                <span className="text-sm font-semibold text-heading">Last 3 mo</span>
                <span className="font-mono text-xs tracking-wider text-muted-foreground">
                  ACTUAL
                </span>
              </div>
            ) : null}
            {layout.columns.map((month, index) => {
              const kind = monthHeaderKind(month, layout.today, layout.planHorizon);
              return (
                <div
                  key={month}
                  className={cn(
                    "flex flex-col justify-end gap-0.5 px-2 py-2.5",
                    kind === "mtd" && "bg-accent",
                    kind === "actual" && "opacity-75",
                    edgeClass(columnEdge(layout, month, index)),
                  )}
                >
                  <span className="text-sm font-semibold text-heading">
                    {columnLabel(month, index)}
                  </span>
                  <span
                    className={cn(
                      "font-mono text-xs tracking-wider",
                      kind === "mtd" ? "text-primary" : "text-muted-foreground",
                    )}
                  >
                    {HEADER_SUB[kind]}
                  </span>
                </div>
              );
            })}
          </div>

          {rows.map((display, gridRow) => {
            const tabCol = gridRow === tabRow ? focus.col : null;
            if (display.kind === "group") {
              const { group } = display;
              return (
                <GroupRow
                  key={`g-${group.id}`}
                  group={group}
                  open={display.open}
                  layout={layout}
                  gridRow={gridRow}
                  tabCol={tabCol}
                  selectedMonth={
                    selectedGroup !== null && selectedGroup.groupKey === group.key
                      ? selectedGroup.month
                      : null
                  }
                  onToggle={onToggleGroup}
                  onSelect={onSelectGroup}
                  onTipShow={show}
                  onTipHide={hide}
                />
              );
            }
            const { row } = display;
            return (
              <ResourceRow
                key={`p-${row.resourceId}`}
                row={row}
                layout={layout}
                gridRow={gridRow}
                tabCol={tabCol}
                selectedMonth={
                  selectedResource !== null && selectedResource.resourceId === row.resourceId
                    ? selectedResource.month
                    : null
                }
                onSelect={onSelectResource}
                onTipShow={show}
                onTipHide={hide}
              />
            );
          })}

          {visibleCount === 0 ? (
            <EmptyState
              title="No Resources match these filters"
              description="Clear the search, tile or Exceptions only filter to see everyone again."
              action={
                <Button variant="outline" size="sm" onClick={onClearFilters}>
                  Clear filters
                </Button>
              }
              className="border-0"
            />
          ) : null}
        </div>
      </section>
      {tip ? <CellTooltip tip={tip} /> : null}
    </>
  );
}
